import { Plus, Clock } from 'lucide-react';
import { useCredits } from '../context/CreditContext';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

// Time-based subscription pill. Replaces the old `<credits-count />`
// display now that the app is subscription-driven.
//
// Active sub  → "Subscribe · 12d 4h left" with the remaining time
//                shown in a HIGH-CONTRAST emerald badge right next to
//                the label so it can't be missed.
// No sub      → "Subscribe" (CTA only, no badge).
//
// Clicking the pill takes the user to /subscription.

function readSubFromWallet(wallet) {
  return (wallet && wallet.subscription) ? wallet.subscription : null;
}

function formatRemaining(sub) {
  if (!sub || !sub.is_active) return null;
  const d = sub.days_remaining || 0;
  const h = sub.hours_remaining || 0;
  const m = sub.minutes_remaining || 0;
  // Be friendly: show the biggest meaningful unit + the next one.
  if (d > 0) return `${d}d ${h}h left`;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m left`;
  return `${m}m left`;
}

export function CreditBalance({ showBuyButton = true, compact = false }) {
  const { wallet, loading } = useCredits();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const sub = readSubFromWallet(wallet);
  const remaining = formatRemaining(sub);

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className={`animate-pulse w-24 h-7 rounded-full ${isDark ? 'bg-gray-700' : 'bg-white/15'}`} />
      </div>
    );
  }

  const pillClass = (extra = '') => `flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
    isDark
      ? 'bg-gray-800 border border-gray-700 text-white hover:bg-gray-700'
      : 'bg-white/15 border border-white/25 text-white hover:bg-white/25'
  } ${extra}`;

  // High-contrast emerald badge so the remaining time pops against
  // both the dark navbar AND the gray menu-row background. We bias
  // saturation by theme so the green still reads on light backdrops.
  const remainingBadgeClass = `ml-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold tabular-nums leading-none ${
    isDark
      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
      : 'bg-emerald-400 text-emerald-950 border border-emerald-300'
  }`;

  if (compact) {
    return (
      <button
        onClick={() => navigate('/subscription')}
        className={pillClass()}
        data-testid="credit-balance"
        title={sub && sub.is_active ? `Subscription active${sub.expires_at ? ` until ${new Date(sub.expires_at).toLocaleString()}` : ''}` : 'No active subscription'}
      >
        <Clock className="w-3.5 h-3.5" />
        <span>Subscribe</span>
        {remaining && (
          <span
            className={remainingBadgeClass}
            data-testid="credit-balance-remaining"
          >
            {remaining}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div
        onClick={() => navigate('/subscription')}
        className={`cursor-pointer ${pillClass()}`}
        data-testid="credit-balance"
      >
        <Clock className="w-3.5 h-3.5" />
        <span>Subscribe</span>
        {remaining && (
          <span
            className={remainingBadgeClass}
            data-testid="credit-balance-remaining"
          >
            {remaining}
          </span>
        )}
      </div>
      {showBuyButton && (
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/subscription')}
          className={`h-7 gap-1 text-xs ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-white/25 text-white hover:bg-white/15'}`}
          data-testid="buy-credits-btn"
        >
          <Plus className="w-3 h-3" />
          {sub && sub.is_active ? 'Extend' : 'Get'}
        </Button>
      )}
    </div>
  );
}

export default CreditBalance;
